/**
 * Unread-alert badge for the Department menu entry (WS-F, workspace
 * docs/guardian/06, 2026-10-23). Reads the single newest team alert and reports
 * whether it is newer than the last time this member opened the Alerts feed
 * (tracked in local storage by `departmentOrg`). Fully fail-closed: any error,
 * a 503 `org_unavailable`, a missing token or a user with no department yields
 * `false`, so the badge never shows spuriously and never throws.
 */
import { useEffect, useRef, useState } from 'react';
import { fetchDepartmentAlerts } from '../api/guardianApi';
import { hasUnseenAlerts, newestAlertAt } from '../utils/departmentOrg';

/**
 * @param {string|null} authToken
 * @param {object|null} user - Guardian user; must have `department` and `id`.
 * @returns {boolean} true when there is at least one alert newer than last seen.
 */
export function useDepartmentAlertBadge(authToken, user) {
  const [hasNew, setHasNew] = useState(false);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);

  const hasDept = Boolean(user?.department);
  const userId = user?.id || null;

  useEffect(() => {
    alive.current = true;
    if (!authToken || !hasDept) {
      setHasNew(false);
      return undefined;
    }
    (async () => {
      try {
        const res = await fetchDepartmentAlerts(authToken, { limit: 1 });
        if (!alive.current) return;
        if (res?.success) {
          setHasNew(hasUnseenAlerts(userId, newestAlertAt(res.data?.alerts)));
        } else {
          setHasNew(false);
        }
      } catch {
        if (alive.current) setHasNew(false);
      }
    })();
    return () => { alive.current = false; };
  }, [authToken, hasDept, userId]);

  return hasNew;
}
